import { formatCurrency } from "../../include/money";
import { useContext, useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { CheckCircle, ErrorOutline } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { callPostApi } from "../../function/ApiCallFunction";
import { DataContext } from "../../context/DataContext";
import { consoleColors, primaryButtonSx } from "../admin/consoleTheme";

const StorePayResponse = () => {
  const navigate = useNavigate();
  const calledRef = useRef(false);
  const { loginData, setLoginData } = useContext(DataContext);
  const [state, setState] = useState({
    loading: true,
    ok: false,
    amount: "",
    txnid: "",
    storeId: "",
    message: "",
  });

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const payload = Object.fromEntries(params.entries());

    const confirmPayment = async () => {
      try {
        const apiResult = await callPostApi("store-pay-success", payload);
        const ok = String(apiResult?.status) === "200";
        setState({
          loading: false,
          ok,
          amount: apiResult?.data?.amount || payload.amount || "",
          txnid: apiResult?.data?.txnid || payload.txnid || "",
          storeId: payload.store_id || "",
          message: ok ? "The amount has been added to your wallet." : (apiResult?.status_message || "Payment could not be confirmed."),
        });

        const storeId = payload.store_id || loginData?.store_info?.store_id;
        if (ok && storeId) {
          const detail = await callPostApi("store-login", { mod: "STORE_DETAIL", data_arr: { store_id: storeId } });
          if (String(detail?.status) === "200" && detail.data?.store_info) {
            setLoginData((prev) => {
              const updated = { ...prev, store_info: { ...prev?.store_info, ...detail.data.store_info } };
              sessionStorage.setItem("loginData", JSON.stringify(updated));
              return updated;
            });
          }
        }
      } catch {
        setState({
          loading: false,
          ok: false,
          amount: payload.amount || "",
          txnid: payload.txnid || "",
          storeId: payload.store_id || "",
          message: "Payment could not be confirmed.",
        });
      }
    };

    confirmPayment();
  }, [loginData?.store_info?.store_id, setLoginData]);

  const walletPath = loginData?.store_info?.cashi_plus_flag === "2" ? "/dashboardPlus/walletPlus" : "/wallet";
  const goWallet = () => navigate(loginData?.store_info?.store_id ? walletPath : "/");

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: consoleColors.page, p: 2 }}>
      <Box sx={{ width: "100%", maxWidth: 440, background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 3, p: { xs: 3, sm: 4 }, textAlign: "center" }}>
        <Typography sx={{ letterSpacing: 1.4, fontSize: 12, color: "#93c5fd", fontWeight: 700, textAlign: "left" }}>CASHI</Typography>

        {state.loading ? (
          <Box sx={{ py: 6 }}>
            <CircularProgress size={28} sx={{ color: consoleColors.accent }} />
            <Typography sx={{ mt: 2, color: consoleColors.muted }}>Confirming your payment</Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ width: 64, height: 64, borderRadius: "50%", mx: "auto", mt: 3, mb: 2, display: "flex", alignItems: "center", justifyContent: "center", background: state.ok ? "#dcfce7" : "#fee2e2", color: state.ok ? "#15803d" : "#b91c1c" }}>
              {state.ok ? <CheckCircle sx={{ fontSize: 34 }} /> : <ErrorOutline sx={{ fontSize: 34 }} />}
            </Box>
            <Typography sx={{ fontSize: 26, fontWeight: 700, color: consoleColors.ink }}>
              {state.ok ? "Payment successful" : "Payment not confirmed"}
            </Typography>
            <Typography sx={{ mt: 1, color: consoleColors.muted, fontSize: 14 }}>{state.message}</Typography>

            <Box sx={{ mt: 3, border: `1px solid ${consoleColors.line}`, borderRadius: 2, textAlign: "left" }}>
              <Row label="Amount" value={state.amount ? formatCurrency(state.amount) : "—"} />
              <Row label="Transaction" value={state.txnid || "—"} />
              <Row label="Store" value={state.storeId || "—"} last />
            </Box>

            <Button variant="contained" fullWidth sx={{ ...primaryButtonSx, mt: 3, py: 1.1 }} onClick={goWallet}>
              {loginData?.store_info?.store_id ? "Back to wallet" : "Back to sign in"}
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};

function Row({ label, value, last }) {
  return (
    <Box sx={{ px: 2, py: 1.4, display: "flex", justifyContent: "space-between", gap: 2, borderBottom: last ? 0 : `1px solid ${consoleColors.line}` }}>
      <Typography sx={{ fontSize: 13, color: consoleColors.muted }}>{label}</Typography>
      <Typography sx={{ fontSize: 14, fontWeight: 650, color: consoleColors.ink, textAlign: "right", wordBreak: "break-all" }}>{value}</Typography>
    </Box>
  );
}

export default StorePayResponse;
